import { Link } from "react-router-dom";
import {
  GraduationCap,
  ExternalLink,
  Mail,
  Phone,
  MessageCircle,
  Clock,
  FileText,
  Linkedin,
  Twitter,
  Facebook,
  Youtube,
  MapPin,
} from "lucide-react";
import { contactConfig } from "@/config/contact";
import { APP_VERSION, APP_VERSION_NOTE } from "@/config/version";

export const Footer = () => {
  return (
    <footer className="border-t border-border bg-muted/30">
      {/* Trust bar */}
      <div className="bg-primary text-primary-foreground">
        <div className="container py-3 flex flex-col md:flex-row items-center justify-between gap-2 text-xs md:text-sm">
          <div className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5" />
            <span>{contactConfig.officeHours}</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
            <a href={`tel:${contactConfig.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 hover:underline">
              <Phone className="h-3.5 w-3.5" /> {contactConfig.phone}
            </a>
            <a href={`mailto:${contactConfig.email}`} className="inline-flex items-center gap-1.5 hover:underline">
              <Mail className="h-3.5 w-3.5" /> {contactConfig.email}
            </a>
            <a href={contactConfig.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:underline">
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </a>
          </div>
        </div>
      </div>

      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
                <GraduationCap className="h-6 w-6 text-primary-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-foreground">we-help.in</span>
                <span className="text-[10px] text-muted-foreground leading-none">A SkillSTEM Initiative</span>
              </div>
            </Link>
            <p className="text-sm text-muted-foreground">
              Helping 10+2 students discover free education and employability opportunities.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/programs" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Explore Programs
                </Link>
              </li>
              <li>
                <Link to="/guidance" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Guidance
                </Link>
              </li>
              <li>
                <Link to="/careers/digital-marketing-intern" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Join Our Team
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Resources</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/guidance/after-12th" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  What after 12th?
                </Link>
              </li>
              <li>
                <Link to="/guidance/avoid-scams" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Avoid Scams
                </Link>
              </li>
              <li>
                <Link to="/guidance/apprenticeships" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Apprenticeships
                </Link>
              </li>
            </ul>
          </div>

          {/* SkillSTEM */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Advanced Pathways</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Ready for career acceleration and structured mentoring?
            </p>
            <a
              href="https://skillstem.in"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              Visit SkillSTEM Academy
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        {/* Unified Ecosystem Strip */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="text-center mb-6">
            <h3 className="text-lg font-bold text-foreground">The SkillSTEM Ecosystem</h3>
            <p className="text-sm text-muted-foreground mt-1">
              One organisation. Multiple pathways. All initiatives of SkillSTEM Academy LLP.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {contactConfig.ecosystem.map((site) => {
              const isPrimary =
                site.url.includes("we-help.in") || site.url === "https://skillstem.in";
              return (
                <a
                  key={site.url}
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={
                    isPrimary
                      ? "group rounded-lg border border-border bg-background/50 p-4 hover:border-primary hover:bg-background transition-colors"
                      : "group rounded-lg border border-border/50 bg-background/20 p-4 opacity-75 hover:opacity-100 hover:border-primary transition-all"
                  }
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={
                        isPrimary
                          ? "font-semibold text-sm text-foreground group-hover:text-primary"
                          : "font-medium text-sm text-muted-foreground group-hover:text-primary"
                      }
                    >
                      {site.name}
                    </span>
                    <ExternalLink className="h-3 w-3 text-muted-foreground group-hover:text-primary shrink-0" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {isPrimary ? site.description : `${site.description} (for institutions & partners)`}
                  </p>
                </a>
              );
            })}
          </div>
        </div>

        {/* Registrations */}
        <div className="mt-8 pt-6 border-t border-border grid gap-3 md:grid-cols-2 text-xs text-muted-foreground">
          {contactConfig.dpiitRecognition && (
            <div className="flex items-start gap-2">
              <span>
                DPIIT Recognition: <span className="font-medium text-foreground">{contactConfig.dpiitRecognition}</span> | Startup India, Government of India
              </span>
            </div>
          )}
          <div className="flex items-start gap-2">
            <span>
              MSME Registered (Udyam): <span className="font-medium text-foreground">{contactConfig.msmeRegistration}</span> | Government of India
            </span>
          </div>
          <div className="flex items-start gap-2 md:col-span-2">
            <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <a href={contactConfig.address.mapsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary">
              {contactConfig.address.line}
            </a>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-xs text-muted-foreground space-y-1">
            <div>© {new Date().getFullYear()} SkillSTEM Academy LLP. All rights reserved.</div>
            <div>LLPIN: {contactConfig.address.llpin} | Registered in India</div>
            <div className="flex items-center gap-2 pt-1">
              <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                Beta
              </span>
              <span className="font-medium text-foreground">Version: {APP_VERSION}</span>
              <Link to="/whats-new" className="text-primary hover:underline">
                What's new
              </Link>
            </div>
            <p className="max-w-md pt-1 text-muted-foreground">{APP_VERSION_NOTE}</p>
            <nav aria-label="Footer secondary" className="flex flex-wrap items-center gap-x-1 gap-y-1 -mx-2">
              <a
                href="https://skillstem.in"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center min-h-11 px-2 py-2 hover:text-primary"
              >
                skillstem.in
              </a>
              <Link to="/ambassador" className="inline-flex items-center min-h-11 px-2 py-2 hover:text-primary">Ambassador Program</Link>
              <Link to="/feedback" className="inline-flex items-center min-h-11 px-2 py-2 hover:text-primary">Share Feedback</Link>
              <a
                href="/?sw=off"
                className="inline-flex items-center min-h-11 px-2 py-2 hover:text-primary"
                title="Clear cached app data and reload"
                onClick={(e) => {
                  e.preventDefault();
                  (async () => {
                    try {
                      if ("serviceWorker" in navigator) {
                        const regs = await navigator.serviceWorker.getRegistrations();
                        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
                      }
                      if (typeof caches !== "undefined") {
                        const names = await caches.keys();
                        await Promise.all(names.map((n) => caches.delete(n).catch(() => {})));
                      }
                    } finally {
                      try {
                        localStorage.removeItem("wh_app_version");
                        sessionStorage.removeItem("wh_sw_cleanup_done");
                      } catch {}
                      window.location.replace("/?sw=off");
                    }
                  })();
                }}
              >
                Reset app
              </a>
            </nav>
          </div>
          <div className="flex flex-col items-center md:items-end gap-3">
            <div className="flex items-center gap-3 text-muted-foreground">
              <a href={contactConfig.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hover:text-primary">
                <Linkedin className="h-4 w-4" />
              </a>
              <a href={contactConfig.social.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="hover:text-primary">
                <Twitter className="h-4 w-4" />
              </a>
              <a href={contactConfig.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-primary">
                <Facebook className="h-4 w-4" />
              </a>
              <a href={contactConfig.social.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-primary">
                <Youtube className="h-4 w-4" />
              </a>
            </div>
            <p className="text-xs text-muted-foreground max-w-md text-center md:text-right">
              ⚠️ we-help.in is an informational guidance platform. We do not conduct training, collect fees, or guarantee outcomes. Program details may change — always verify on official provider websites.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
